import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  Skeleton,
  Typography
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { TreeItem, TreeView } from '@mui/lab'
import { useGlobalMessageHandler } from 'components'
import { useSessionContext } from 'session'
import {
  type ExecOrgDescendantItem,
  type ExecOrgRef,
  getExecutiveOrgChildren,
  getExecutiveOrgDescendants
} from 'services/executives'

type HierarchyNode = ExecOrgRef & { hasChildren?: boolean }

const formatOrgLabel = (name: string, code: string | null): string =>
  code != null && code !== '' ? `${name} (${code})` : name

interface Props {
  // The org being viewed — the root of the displayed tree. Its direct children
  // load on mount; deeper levels load lazily as the user expands them.
  org: ExecOrgRef
  onReorderChildren?: () => void
  reloadToken?: number
}

export const ExecutiveOrgHierarchyCard: React.FC<Props> = ({ org, onReorderChildren, reloadToken = 0 }) => {
  const { openNewTab } = useSessionContext()
  const { showErrorMessage } = useGlobalMessageHandler()
  const [childrenByParent, setChildrenByParent] = useState<Map<number, ExecOrgDescendantItem[]>>(new Map())
  const [loadingIds, setLoadingIds] = useState<Set<number>>(new Set())
  const [erroredIds, setErroredIds] = useState<Set<number>>(new Set())
  const [expanded, setExpanded] = useState<string[]>([String(org.id)])
  const [loadingAll, setLoadingAll] = useState(false)
  const inFlight = useRef<Set<number>>(new Set())

  const loadChildren = useCallback((parentId: number): void => {
    if (inFlight.current.has(parentId)) return
    inFlight.current.add(parentId)
    setLoadingIds(prev => new Set(prev).add(parentId))
    setErroredIds(prev => { const next = new Set(prev); next.delete(parentId); return next })
    getExecutiveOrgChildren(parentId)
      .then(items => { setChildrenByParent(prev => new Map(prev).set(parentId, items)) })
      .catch(() => { setErroredIds(prev => new Set(prev).add(parentId)) })
      .finally(() => {
        inFlight.current.delete(parentId)
        setLoadingIds(prev => { const next = new Set(prev); next.delete(parentId); return next })
      })
  }, [])

  // (Re)load the root's children whenever the viewed org changes, resetting the
  // tree to just-the-root expanded.
  useEffect(() => {
    inFlight.current = new Set()
    setChildrenByParent(new Map())
    setErroredIds(new Set())
    setExpanded([String(org.id)])
    loadChildren(org.id)
  }, [org.id, loadChildren])

  const lastReloadToken = useRef(reloadToken)
  useEffect(() => {
    if (reloadToken === lastReloadToken.current) return
    lastReloadToken.current = reloadToken
    loadChildren(org.id)
  }, [reloadToken, org.id, loadChildren])

  const handleToggle = (_event: React.SyntheticEvent, nodeIds: string[]): void => {
    setExpanded(nodeIds)
    nodeIds.forEach(nodeId => {
      const id = Number(nodeId)
      if (!childrenByParent.has(id)) loadChildren(id)
    })
  }

  const handleExpandAll = (): void => {
    setLoadingAll(true)
    getExecutiveOrgDescendants(org.id)
      .then(items => {
        const byParent = new Map<number, ExecOrgDescendantItem[]>()
        byParent.set(org.id, [])
        items.forEach(item => { byParent.set(item.id, []) })
        items.forEach(item => {
          if (item.parentId == null) return
          byParent.get(item.parentId)?.push(item)
        })
        setChildrenByParent(byParent)
        setErroredIds(new Set())
        setExpanded([String(org.id), ...items.map(item => String(item.id))])
      })
      .catch(() => { showErrorMessage('Failed to expand the full hierarchy.') })
      .finally(() => { setLoadingAll(false) })
  }

  const handleCollapseAll = (): void => { setExpanded([]) }

  // Self-recursive: a node renders its cached children, or — when its level is
  // not yet loaded — a stub so the expand arrow appears (deeper nodes show the
  // arrow when the API says hasChildren; `isRoot` forces it for the viewed org
  // until its first level arrives). A leaf renders nothing, so it gets no arrow.
  const renderNode = (node: HierarchyNode, isRoot: boolean): JSX.Element => {
    const loaded = childrenByParent.get(node.id)
    let children: React.ReactNode = null
    if (loaded != null) {
      children = loaded.length > 0 ? loaded.map(child => renderNode(child, false)) : null
    } else if (erroredIds.has(node.id)) {
      children = <TreeItem nodeId={`${node.id}-error`} label={<Box sx={{ color: 'error.main' }}>Failed to load.</Box>} />
    } else if (isRoot || node.hasChildren === true || loadingIds.has(node.id)) {
      children = <TreeItem nodeId={`${node.id}-stub`} label={<Skeleton width={160} />} />
    }
    return (
      <TreeItem
        key={node.id}
        nodeId={String(node.id)}
        label={
          <Box
            component='span'
            onClick={event => { event.stopPropagation(); openNewTab(`/us/executives/orgs/${node.id}`) }}
            sx={{ cursor: 'pointer', fontWeight: node.id === org.id ? 'bold' : 'normal' }}
          >
            {formatOrgLabel(node.name, node.code)}
          </Box>
        }
      >
        {children}
      </TreeItem>
    )
  }

  const rootLoaded = childrenByParent.get(org.id)

  return (
    <Card sx={{ borderRadius: '12px', width: '100%' }}>
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant='h6'>Organization Hierarchy</Typography>
        </AccordionSummary>
        <AccordionDetails>
          {erroredIds.has(org.id)
            ? <Box>Failed to load organization hierarchy.</Box>
            : (
              <>
                <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                  <Button size='small' onClick={handleExpandAll} disabled={loadingAll}>Expand All</Button>
                  <Button size='small' onClick={handleCollapseAll} disabled={loadingAll}>Collapse All</Button>
                  {onReorderChildren != null && (
                    <Button
                      size='small'
                      onClick={onReorderChildren}
                      disabled={loadingAll || rootLoaded?.length === 0}
                    >
                      Reorder Children
                    </Button>
                  )}
                </Box>
                <TreeView
                  expanded={expanded}
                  onNodeToggle={handleToggle}
                  defaultCollapseIcon={<ExpandMoreIcon />}
                  defaultExpandIcon={<ChevronRightIcon />}
                  sx={{ '& .MuiTreeItem-label': { fontSize: '18px' } }}
                >
                  {renderNode(org, true)}
                </TreeView>
                {rootLoaded?.length === 0 && (
                  <Box sx={{ pl: 4, pt: 1, color: 'text.secondary' }}>No sub-organizations.</Box>
                )}
              </>
              )}
        </AccordionDetails>
      </Accordion>
    </Card>
  )
}
